import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import { reactPlugin } from '@datadog/browser-rum-react'
import config from 'constants/config'
import { reportClientError } from './errorReportingService'

let initialized = false

export function initUserMonitoring() {
  if (initialized) return
  initialized = true

  const applicationId = config.DATADOG_RUM_APPLICATION_ID
  const clientToken = config.DATADOG_RUM_CLIENT_TOKEN

  if (!applicationId || !clientToken) {
    return
  }

  const site = config.DATADOG_RUM_SITE || 'ap2.datadoghq.com'
  // RUM service is the bare service name, NOT the APM DD_SERVICE with component
  // suffix (-main). RUM-to-APM trace correlation uses allowedTracingUrls
  // propagation headers, not service name matching.
  const service = 'www-le-customer'
  const env = config.APP_ENV || 'unknown'
  const commitSha = config.COMMIT_SHA || undefined
  const version = commitSha ? commitSha.substring(0, 7) : 'unknown'

  datadogRum.init({
    applicationId,
    clientToken,
    site,
    service,
    env,
    version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    defaultPrivacyLevel: 'mask-user-input',
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    allowedTracingUrls: [],
    // react-router v5 — router tracking not supported until v6+
    plugins: [reactPlugin({ router: false })],
  })

  datadogLogs.init({
    clientToken,
    site,
    service,
    env,
    version,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error', 'warn'],
    sessionSampleRate: 100,
  })
}

export function setMonitoringUser(userId: string | number | undefined | null) {
  if (typeof window === 'undefined') return
  try {
    if (userId != null && userId !== '') {
      const user = { id: String(userId) }
      datadogRum.setUser(user)
      datadogLogs.setUser(user)
    } else {
      datadogRum.clearUser()
      datadogLogs.clearUser()
    }
  } catch {
    // Silently fail
  }
}

export function reportLog(message: string, data: any, level: 'info' | 'warn' | 'error' = 'error') {
  if (typeof window === 'undefined') return
  try {
    const context = data != null && typeof data === 'object' ? data : { value: data }

    if (level === 'error') {
      datadogRum.addError(new Error(message), context)
      datadogLogs.logger.error(message, context)
    } else {
      datadogRum.addAction(message, { ...context, level })
      datadogLogs.logger[level](message, context)
    }
  } catch {
    try {
      reportClientError(new Error('reportLog: client logging failed'), {
        originalMessage: message,
        attemptedLevel: level,
      })
    } catch {
      // Both monitoring and Sentry failed; don't crash the app
    }
  }
}

export function createLoggerWithContext(baseContext: Record<string, any>) {
  return (message: string, level: 'info' | 'warn' | 'error', additionalContext: Record<string, any> = {}) => {
    reportLog(message, { ...baseContext, ...additionalContext }, level)
  }
}
